import { Geometry } from 'geojson';

import { persistProjectFile } from '../../services/db/filesystem.service';
import { GeometryValidationParams } from '../../services/validation.service';
import { CoreoAssociateMap, CoreoAttribute, CoreoForm, CoreoRecord, CoreoRecordAttachment } from '../../types';
import { TypeKeys } from '../actions';
import { formsSupportClearRestoreMedia } from '../formsSupport/formsSupport.actions';
import { getAppAttributes, getAppAttributesForForm, getAppForm, getAppId, getFormChildSurveysToKeepInBounds, getFormsCurrentForm, getFormsForms, getFormsSupportRestoreMediaAttachmentId, getFormsSupportRestoreMediaAttributeId, getFormsSupportRestoreMediaUrl, } from '../selectors';
import { FormState } from './forms.reducer';

/******************************************************************************
 * ACTIONS
 *****************************************************************************/
export interface FormsUpdateRecordDataAction {
  type: TypeKeys.FORM_UPDATE_RECORD_DATA;
  path: string;
  value: any;
}

export interface FormsAddRecordAssociateAction {
  type: TypeKeys.FORM_ADD_RECORD_ASSOCIATE;
  associationAttributeId: number;
  record: CoreoRecord;
}

export interface FormsUpdateRecordAssociateAction {
  type: TypeKeys.FORM_UPDATE_RECORD_ASSOCIATE;
  associationAttributeId: number;
  record: CoreoRecord;
  index: number;
}

export interface FormsRemoveRecordAssociateAction {
  type: TypeKeys.FORM_REMOVE_RECORD_ASSOCIATE;
  associationAttributeId: number;
  index: number;
  attributes: CoreoAttribute[];
}

export interface FormsAddRecordAssociatedToAction {
  type: TypeKeys.FORM_ADD_RECORD_ASSOCIATED_TO;
  associationAttributeId: number;
  record: CoreoRecord;
}

export interface FormsRemoveRecordAssociatedToAction {
  type: TypeKeys.FORM_REMOVE_RECORD_ASSOCIATED_TO;
  associationAttributeId: number;
  index: number;
}

export interface FormsRestoreRecordAssociatedToAction {
  type: TypeKeys.FORM_RESTORE_RECORD_ASSOCIATED_TO;
  associationAttributeId: number;
  index: number;
}

export interface FormsCloneRecordAssociateAction {
  type: TypeKeys.FORM_CLONE_RECORD_ASSOCIATE;
  associationAttributeId: number;
  index: number;
  userId: number;
  attributes: CoreoAttribute[];
}

export interface FormsRestoreRecordAssociateAction {
  type: TypeKeys.FORM_RESTORE_RECORD_ASSOCIATE;
  associationAttributeId: number;
  index: number;
  attributes: CoreoAttribute[];
}

export interface FormsUpdateRecordGeometryAction {
  type: TypeKeys.FORM_UPDATE_RECORD_GEOMETRY;
  geometry: Geometry;
}

export interface FormsAddRecordAttachmentAction {
  type: TypeKeys.FORM_ADD_RECORD_ATTACHMENT;
  attachment: CoreoRecordAttachment;
}

export interface FormsReplaceRecordAttachmentAction {
  type: TypeKeys.FORM_REPLACE_RECORD_ATTACHMENT;
  attachment: CoreoRecordAttachment;
  previousAttachment?: CoreoRecordAttachment;
}

export interface FormsRemoveRecordAttachmentAction {
  type: TypeKeys.FORM_REMOVE_RECORD_ATTACHMENT;
  attachment: CoreoRecordAttachment;
}

export interface FormsSplitRecordAction {
  type: TypeKeys.FORM_SPLIT_RECORD;
  newGeometry: Geometry;
  newRecords: CoreoRecord[];
  updatedAssociates: CoreoAssociateMap;
}

export interface FormsResetAction {
  type: TypeKeys.FORM_RESET;
  record?: CoreoRecord;
}

export interface FormsNextAction {
  type: TypeKeys.FORM_NEXT;
}

export interface FormsPreviousAction {
  type: TypeKeys.FORM_PREV;
}

export interface FormsAddChildRecordAction {
  type: TypeKeys.FORM_ADD_CHILD_RECORD;
  record: CoreoRecord;
}

export interface FormsDismissAction {
  type: TypeKeys.FORM_DISMISS;
}

export interface FormsBlockAction {
  type: TypeKeys.FORM_BLOCK;
}

export interface FormsUnblockAction {
  type: TypeKeys.FORM_UNBLOCK;
}

export interface FormsCompleteAction {
  type: TypeKeys.FORM_COMPLETE;
}

export interface FormsFinishedAction {
  type: TypeKeys.FORM_FINISHED;
}

export interface FormsDismissAllAction {
  type: TypeKeys.FORM_DISMISS_ALL;
}

export interface FormClearDirtyAction {
  type: TypeKeys.FORM_CLEAR_DIRTY;
}

export interface FormsInitializeAction {
  type: TypeKeys.FORM_INITIALIZE;
  form: CoreoForm;
  attributes: CoreoAttribute[];
  projectId: number;
  /** `null` opens a brand-new form; the reducer seeds the stack record (see `initForm` when `record` is falsy). */
  record: CoreoRecord | null;
  subformAttributeId: number;
  subformIdx: number;
  geometryValidationParams: GeometryValidationParams;
}

export interface FormsHydrateAction {
  type: TypeKeys.FORM_HYDRATE;
  form: FormState;
  attributes: CoreoAttribute[];
  projectId: number;
}

export interface FormsMarkAsReadyAction {
  type: TypeKeys.FORM_MARK_AS_READY;
  attributes: CoreoAttribute[];
}

export type FormsActions =
  | FormsUpdateRecordDataAction
  | FormsAddRecordAssociateAction
  | FormsUpdateRecordAssociateAction
  | FormsRemoveRecordAssociateAction
  | FormsAddRecordAssociatedToAction
  | FormsRemoveRecordAssociatedToAction
  | FormsRestoreRecordAssociateAction
  | FormsRestoreRecordAssociatedToAction
  | FormsCloneRecordAssociateAction
  | FormsUpdateRecordGeometryAction
  | FormsAddRecordAttachmentAction
  | FormsReplaceRecordAttachmentAction
  | FormsRemoveRecordAttachmentAction
  | FormsSplitRecordAction
  | FormsResetAction
  | FormsNextAction
  | FormsPreviousAction
  | FormsAddChildRecordAction
  | FormsDismissAction
  | FormsBlockAction
  | FormsUnblockAction
  | FormsCompleteAction
  | FormsFinishedAction
  | FormsDismissAllAction
  | FormClearDirtyAction
  | FormsInitializeAction
  | FormsHydrateAction
  | FormsMarkAsReadyAction;

/******************************************************************************
 * ACTION CREATORS
 *****************************************************************************/
export const updateFormRecordData = (path: string, value: any): FormsUpdateRecordDataAction => {
  return {
    type: TypeKeys.FORM_UPDATE_RECORD_DATA,
    path,
    value
  };
};

/**
 * Associates
 */
export const addFormRecordAssociate = (associationAttributeId: number, record: CoreoRecord): FormsAddRecordAssociateAction => ({
  type: TypeKeys.FORM_ADD_RECORD_ASSOCIATE,
  associationAttributeId,
  record
});

export const removeFormRecordAssociate = (associationAttributeId: number, index: number, attributes: CoreoAttribute[]): FormsRemoveRecordAssociateAction => ({
  type: TypeKeys.FORM_REMOVE_RECORD_ASSOCIATE,
  associationAttributeId,
  index,
  attributes
});

/**
 * Associated Tos
 */
export const addFormRecordAssociatedTo = (associationAttributeId: number, record: CoreoRecord): FormsAddRecordAssociatedToAction => ({
  type: TypeKeys.FORM_ADD_RECORD_ASSOCIATED_TO,
  associationAttributeId,
  record
});

export const removeFormRecordAssociatedTo = (associationAttributeId: number, index: number): FormsRemoveRecordAssociatedToAction => ({
  type: TypeKeys.FORM_REMOVE_RECORD_ASSOCIATED_TO,
  associationAttributeId,
  index
});

export const restoreFormRecordAssociate = (associationAttributeId: number, index: number, attributes: CoreoAttribute[]): FormsRestoreRecordAssociateAction => ({
  type: TypeKeys.FORM_RESTORE_RECORD_ASSOCIATE,
  associationAttributeId,
  index,
  attributes
});

export const restoreFormRecordAssociatedTo = (associationAttributeId: number, index: number): FormsRestoreRecordAssociatedToAction => ({
  type: TypeKeys.FORM_RESTORE_RECORD_ASSOCIATED_TO,
  associationAttributeId,
  index,
});

export const cloneFormRecordAssociate = (
  associationAttributeId: number,
  index: number,
  userId: number,
  attributes: CoreoAttribute[]
): FormsCloneRecordAssociateAction => ({
  type: TypeKeys.FORM_CLONE_RECORD_ASSOCIATE,
  associationAttributeId,
  index,
  userId,
  attributes
});

export const updateFormRecordGeometry = (geometry: any): FormsUpdateRecordGeometryAction => ({
  type: TypeKeys.FORM_UPDATE_RECORD_GEOMETRY,
  geometry
});

export const addFormRecordAttachment = (attachment: CoreoRecordAttachment): FormsAddRecordAttachmentAction => ({
  type: TypeKeys.FORM_ADD_RECORD_ATTACHMENT,
  attachment
});

export const replaceFormRecordAttachment = (
  attachment: CoreoRecordAttachment,
  previousAttachment?: CoreoRecordAttachment
): FormsReplaceRecordAttachmentAction => ({
  type: TypeKeys.FORM_REPLACE_RECORD_ATTACHMENT,
  attachment,
  previousAttachment
});

export const removeFormRecordAttachment = (attachment: CoreoRecordAttachment): FormsRemoveRecordAttachmentAction => ({
  type: TypeKeys.FORM_REMOVE_RECORD_ATTACHMENT,
  attachment
});

export const updateFormRecordSplit = (newGeometry: Geometry, newRecords: CoreoRecord[], updatedAssociates: CoreoAssociateMap): FormsSplitRecordAction => ({
  type: TypeKeys.FORM_SPLIT_RECORD,
  newGeometry,
  newRecords,
  updatedAssociates
});

export const resetForm = (record?: CoreoRecord): FormsResetAction => ({
  type: TypeKeys.FORM_RESET,
  record
});

export const formNextPage = (): FormsNextAction => ({
  type: TypeKeys.FORM_NEXT
});

export const formPreviousPage = (): FormsPreviousAction => ({
  type: TypeKeys.FORM_PREV
});

export const formAddChildRecord = (record: CoreoRecord): FormsAddChildRecordAction => ({
  type: TypeKeys.FORM_ADD_CHILD_RECORD,
  record
});

export const formComplete = (): FormsCompleteAction => ({
  type: TypeKeys.FORM_COMPLETE
});

export const formFinished = (): FormsFinishedAction => ({
  type: TypeKeys.FORM_FINISHED
});

export const formDismiss = (): FormsDismissAction => ({
  type: TypeKeys.FORM_DISMISS
});

export const formBlock = (): FormsBlockAction => ({
  type: TypeKeys.FORM_BLOCK
});

export const formUnblock = (): FormsUnblockAction => ({
  type: TypeKeys.FORM_UNBLOCK
});

export const formInit = (
  form: CoreoForm,
  attributes: CoreoAttribute[],
  projectId: number,
  record: CoreoRecord | null,
  subformAttributeId: number,
  subformIdx: number,
  geometryValidationParams: GeometryValidationParams): FormsInitializeAction => ({
    type: TypeKeys.FORM_INITIALIZE,
    form,
    attributes,
    projectId,
    record,
    subformAttributeId,
    subformIdx,
    geometryValidationParams
  });

export const formHydrate = (form: FormState, attributes: CoreoAttribute[], projectId: number): FormsHydrateAction => ({
  type: TypeKeys.FORM_HYDRATE,
  form,
  attributes,
  projectId
});

export const formDismissAll = (): FormsDismissAllAction => ({
  type: TypeKeys.FORM_DISMISS_ALL
});

export const formClearDirty = (): FormClearDirtyAction => ({
  type: TypeKeys.FORM_CLEAR_DIRTY
});

export const formMarkAsReady = (attributes: CoreoAttribute[]): FormsMarkAsReadyAction => ({
  type: TypeKeys.FORM_MARK_AS_READY,
  attributes
});

/******************************************************************************
 * THUNKS
 *****************************************************************************/
export const loadForm = (id: number, record: CoreoRecord | null, subformAttributeId: number, subformIdx: number) => (dispatch, getState) => {

  const state = getState();
  const form = getAppForm(id)(state);

  if (!form) {
    return;
  }

  // A child form can only be pushed on top of the form that owns its child question;
  // anything else (e.g. two child rows tapped in quick succession) would corrupt the stack.
  if (subformAttributeId != null) {
    const parent = getFormsCurrentForm(state);
    if (!parent?.attributes?.some(a => a.id === subformAttributeId)) {
      console.error('loadForm: current form does not own subform attribute; refusing to open child form', {
        subformAttributeId,
        parentFormId: parent?.id
      });
      return;
    }
  }

  const attributes = getAppAttributesForForm(id)(state);
  const projectId = getAppId(state);

  const childSurveysToEnforce = getFormChildSurveysToKeepInBounds(id)(state);

  const geometryValidationParams: GeometryValidationParams = {
    childSurveysToEnforce
  }

  dispatch(formInit(form, attributes, projectId, record, subformAttributeId, subformIdx, geometryValidationParams));

  return getFormsCurrentForm(getState());
};

export const loadExistingForms = () => async (dispatch, getState) => {


  const forms = getFormsForms(getState());
  if (forms.length === 0) {
    return [];
  }

  // Or if we've finished
  if (forms.length === 1 && forms[0].finished) {
    dispatch(formDismissAll());
    return [];
  }

  const state = getState();
  const projectId = getAppId(state);
  const restoreMediaAttributeId = getFormsSupportRestoreMediaAttributeId(state);
  const restoreMediaAttachmentId = getFormsSupportRestoreMediaAttachmentId(state);

  for (const f of forms) {
    const form = getAppForm(f.id)(state);
    if (!form) {
      // If we can't find the form, abort everything
      dispatch(formDismissAll());
      return [];
    }
    const attributes = getAppAttributesForForm(f.id)(state);
    const formState: FormState = {
      ...form,
      ...f
    };
    dispatch(formHydrate(formState, attributes, projectId));

    if (restoreMediaAttributeId && typeof attributes.find(a => a.id === restoreMediaAttributeId) !== 'undefined') {
      // Restore the media
      const url = getFormsSupportRestoreMediaUrl(state);
      if (url) {
        const mimeType = 'image/jpeg';
        const fileLocation = await persistProjectFile(projectId, url, mimeType);

        const attachment = {
          id: restoreMediaAttachmentId ?? Number(Array.from(window.crypto.getRandomValues(new Uint8Array(13)), byte => byte % 10).join('')),
          attributeId: restoreMediaAttributeId,
          fileLocation,
          url: fileLocation,
          mimeType,
          sort: 0
        };

        if (restoreMediaAttachmentId) {
          console.log('Replacing attachment');
          dispatch(replaceFormRecordAttachment(attachment));
        } else {
          console.log('Adding attachment');
          dispatch(addFormRecordAttachment(attachment));
        }
      }
    }
    dispatch(formsSupportClearRestoreMedia());
  }
  return forms;
};

export const markFormAsReady = () => (dispatch, getState) => {
  const state = getState();
  dispatch(formMarkAsReady(getAppAttributes(state)));
};